import React from "react";
import { CloseIcon, FilterIcon } from "@components/Icons";
import {
  Stack,
  Drawer,
  Typography,
  IconButton,
  TextField,
  useMediaQuery,
  useTheme,
  Skeleton,
} from "@mui/material";
import { useFormik } from "formik";
import { useState, useEffect, Fragment, CSSProperties, useRef } from "react";
import { initialValues, validationSchema } from "../models/schema";
import Button from "@components/Button";
import { FilterAltOutlined } from "@mui/icons-material";
import { useMyDriversContext } from "@context/MyDriversContext";
import { useAuth } from "@context/auth/AuthContext";
import DropdownSelectAll from "@components/DropdownSelectAll";

export const FilterButton = () => {
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [companies, setCompanies] = useState<string[]>([]);

  const ref = useRef<HTMLInputElement | null>(null);

  const theme = useTheme();
  const styles = useStyles();

  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));

  const { listCNPJ } = useAuth();

  const options = listCNPJ.map((item) => ({
    label: item.companyName,
    value: item.CNPJ,
  }));

  const {
    loading,
    filters,
    setPage,
    setFilters,
    fetchAllDrivers,
  } = useMyDriversContext();

  const { handleSubmit, handleBlur, setFieldValue, errors } = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: () => {
      setPage(0);
      fetchAllDrivers();
    },
  });

  const handleSetDriverQuery = (value: string) => {
    setFieldValue("driverQuery", value);
    setFilters((prevState) => ({
      ...prevState,
      driverQuery: value,
    }));
  };

  const handleMountList = (value: string[]) => {
    setCompanies(value);

    const listCompanyDocuments = value.join(",");

    setFilters((prevState) => ({
      ...prevState,
      companyDocuments: listCompanyDocuments,
    }));
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      ref?.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [openDrawer]);

  return (
    <Fragment>
      {loading ? (
        <Skeleton animation="wave" height="5rem" width="10rem" />
      ) : (
        <Button
          fullWidth={isMobile}
          variant="outlined"
          startIcon={<FilterAltOutlined />}
          onClick={() => setOpenDrawer(true)}
        >
          Filtrar
        </Button>
      )}

      <Drawer
        open={openDrawer}
        onClose={() => handleCloseDrawer()}
        anchor="right"
        elevation={16}
        sx={{ padding: "2rem", zIndex: 1201 }}
      >
        <Stack sx={styles.drawer_root}>
          <Stack sx={styles.drawer_header}>
            <Typography sx={styles.drawer_title}>Filtrar dados</Typography>

            <IconButton onClick={() => setOpenDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Stack alignSelf="center">
            <FilterIcon />
          </Stack>

          <Stack sx={styles.drawer_content}>
            <Stack sx={{ width: "100%" }}>
              <Typography
                color="shadesOfDark.dark"
                sx={{ mb: 0.5, fontWeight: 700 }}
              >
                Nome ou e-mail
              </Typography>
              <TextField
                fullWidth
                inputRef={ref}
                placeholder="Nome ou e-mail"
                value={filters.driverQuery}
                onChange={({ target: { value } }) =>
                  handleSetDriverQuery(value)
                }
                onBlur={handleBlur("driverQuery")}
                error={Boolean(errors.driverQuery)}
                helperText={errors.driverQuery}
              />
            </Stack>

            <Stack sx={{ width: "100%" }}>
              <Typography
                color="shadesOfDark.dark"
                sx={{ mb: 0.5, fontWeight: 700 }}
              >
                Razão social
              </Typography>
              <DropdownSelectAll
                options={options}
                value={companies}
                isSubTitle
                isCNPJ
                label=""
                onChange={(value) => handleMountList(value)}
              />
            </Stack>
          </Stack>

          <Button
            type="submit"
            variant="contained"
            onClick={() => handleSubmit()}
            sx={{ alignSelf: "center" }}
          >
            Filtrar
          </Button>
        </Stack>
      </Drawer>
    </Fragment>
  );
};

const useStyles = (): Record<string, CSSProperties> => ({
  drawer_root: { width: "30rem", rowGap: "3rem" },
  drawer_content: {
    rowGap: "3rem",
    padding: "2rem",
    alignItems: "center",
  },
  drawer_header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2rem",
  },
  drawer_title: { fontWeight: "bold", fontSize: "1.5rem" },
});
